import type { Dispatch, ReactNode } from 'react';
import { createContext, useContext, useEffect, useReducer } from 'react';

import { STORAGE_KEYS } from '../constants/index.js';
import type { CallerTab, KeyValuePair, SavedConfig } from '../types/caller.js';
import { DEFAULT_HEADERS } from '../types/caller.js';

export interface LastResponse {
  status: number;
  statusText: string;
  headers: Record<string, string>;
  body: string;
  durationMs: number;
  cv: string | null;
}

interface WorkingConfig {
  url: string;
  method: SavedConfig['method'];
  headers: KeyValuePair[];
  params: KeyValuePair[];
  vars: KeyValuePair[];
  body: string;
  name: string;
}

interface CallerState {
  savedConfigs: SavedConfig[];
  openTabs: CallerTab[];
  activeTabId: string | null;
  workingConfigs: Record<string, WorkingConfig>;
  emitSigsLocally: boolean;
  responses: Record<string, LastResponse>;
}

type CallerAction =
  | { type: 'LOAD_CONFIGS'; configs: SavedConfig[] }
  | { type: 'SAVE_CONFIG'; config: SavedConfig }
  | { type: 'DELETE_CONFIG'; id: string }
  | { type: 'RENAME_CONFIG'; id: string; name: string }
  | { type: 'CLONE_CONFIG'; id: string }
  | { type: 'OPEN_TAB'; configId: string }
  | { type: 'CLOSE_TAB'; configId: string }
  | { type: 'SET_ACTIVE_TAB'; configId: string }
  | { type: 'SET_EMIT_SIGS'; value: boolean }
  | { type: 'SET_RESPONSE'; configId: string; response: LastResponse }
  | {
      type: 'UPDATE_WORKING_CONFIG';
      configId: string;
      patch: Partial<WorkingConfig>;
    }
  | { type: 'ADD_NEW_TAB' };

function makeNewConfig(): SavedConfig {
  return {
    id: crypto.randomUUID(),
    name: 'New Request',
    url: '',
    method: 'POST',
    headers: DEFAULT_HEADERS.map((h, i) => ({ ...h, id: String(i + 1) })),
    params: [],
    vars: [],
    body: '',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

function callerReducer(state: CallerState, action: CallerAction): CallerState {
  switch (action.type) {
    case 'LOAD_CONFIGS':
      return { ...state, savedConfigs: action.configs };

    case 'SAVE_CONFIG': {
      const exists = state.savedConfigs.some((c) => c.id === action.config.id);
      return {
        ...state,
        savedConfigs: exists
          ? state.savedConfigs.map((c) =>
              c.id === action.config.id ? action.config : c,
            )
          : [...state.savedConfigs, action.config],
        openTabs: state.openTabs.map((t) =>
          t.configId === action.config.id ? { ...t, isDirty: false } : t,
        ),
      };
    }

    case 'RENAME_CONFIG': {
      const savedConfigs = state.savedConfigs.map((c) =>
        c.id === action.id
          ? { ...c, name: action.name, updatedAt: new Date().toISOString() }
          : c,
      );
      const workingConfigs = state.workingConfigs[action.id]
        ? {
            ...state.workingConfigs,
            [action.id]: {
              ...state.workingConfigs[action.id]!,
              name: action.name,
            },
          }
        : state.workingConfigs;
      return { ...state, savedConfigs, workingConfigs };
    }

    case 'CLONE_CONFIG': {
      const source = state.savedConfigs.find((c) => c.id === action.id);
      if (!source) return state;
      const cloned: SavedConfig = {
        ...source,
        id: crypto.randomUUID(),
        name: `Copy of ${source.name}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      const insertIdx = state.savedConfigs.findIndex((c) => c.id === action.id);
      const savedConfigs = [...state.savedConfigs];
      savedConfigs.splice(insertIdx + 1, 0, cloned);
      return { ...state, savedConfigs };
    }

    case 'DELETE_CONFIG': {
      const savedConfigs = state.savedConfigs.filter((c) => c.id !== action.id);
      const openTabs = state.openTabs.filter((t) => t.configId !== action.id);
      const activeTabId =
        state.activeTabId === action.id
          ? (openTabs[0]?.configId ?? null)
          : state.activeTabId;
      const responses = { ...state.responses };
      delete responses[action.id];
      return { ...state, savedConfigs, openTabs, activeTabId, responses };
    }

    case 'OPEN_TAB': {
      const alreadyOpen = state.openTabs.some(
        (t) => t.configId === action.configId,
      );
      const config = state.savedConfigs.find((c) => c.id === action.configId);
      if (!config) return state;
      const workingConfigs = alreadyOpen
        ? state.workingConfigs
        : {
            ...state.workingConfigs,
            [action.configId]: {
              url: config.url,
              method: config.method,
              headers: config.headers,
              params: config.params,
              vars: config.vars ?? [],
              body: config.body,
              name: config.name,
            },
          };
      return {
        ...state,
        openTabs: alreadyOpen
          ? state.openTabs
          : [...state.openTabs, { configId: action.configId, isDirty: false }],
        activeTabId: action.configId,
        workingConfigs,
      };
    }

    case 'CLOSE_TAB': {
      const openTabs = state.openTabs.filter(
        (t) => t.configId !== action.configId,
      );
      const activeTabId =
        state.activeTabId === action.configId
          ? (openTabs[openTabs.length - 1]?.configId ?? null)
          : state.activeTabId;
      const workingConfigs = { ...state.workingConfigs };
      delete workingConfigs[action.configId];
      return { ...state, openTabs, activeTabId, workingConfigs };
    }

    case 'SET_ACTIVE_TAB':
      return { ...state, activeTabId: action.configId };

    case 'SET_EMIT_SIGS':
      return { ...state, emitSigsLocally: action.value };

    case 'SET_RESPONSE':
      return {
        ...state,
        responses: { ...state.responses, [action.configId]: action.response },
      };

    case 'UPDATE_WORKING_CONFIG': {
      const current = state.workingConfigs[action.configId];
      if (!current) return state;
      const updated = { ...current, ...action.patch };
      const openTabs = state.openTabs.map((t) =>
        t.configId === action.configId ? { ...t, isDirty: true } : t,
      );
      return {
        ...state,
        openTabs,
        workingConfigs: { ...state.workingConfigs, [action.configId]: updated },
      };
    }

    case 'ADD_NEW_TAB': {
      const newConfig = makeNewConfig();
      const workingConfig: WorkingConfig = {
        url: newConfig.url,
        method: newConfig.method,
        headers: newConfig.headers,
        params: newConfig.params,
        vars: newConfig.vars,
        body: newConfig.body,
        name: newConfig.name,
      };
      return {
        ...state,
        savedConfigs: [...state.savedConfigs, newConfig],
        openTabs: [
          ...state.openTabs,
          { configId: newConfig.id, isDirty: false },
        ],
        activeTabId: newConfig.id,
        workingConfigs: {
          ...state.workingConfigs,
          [newConfig.id]: workingConfig,
        },
      };
    }

    default:
      return state;
  }
}

const CallerContext = createContext<{
  state: CallerState;
  dispatch: Dispatch<CallerAction>;
} | null>(null);

function loadInitialState(): CallerState {
  let savedConfigs: SavedConfig[] = [];
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.SIGS_SAVED_CONFIGS);
    if (stored) {
      savedConfigs = JSON.parse(stored) as SavedConfig[];
    }
  } catch {
    // ignore malformed stored data
  }
  return {
    savedConfigs,
    openTabs: [],
    activeTabId: null,
    workingConfigs: {},
    emitSigsLocally: false,
    responses: {},
  };
}

export function CallerProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(
    callerReducer,
    undefined,
    loadInitialState,
  );

  // Persist saved configs to localStorage on change
  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEYS.SIGS_SAVED_CONFIGS,
      JSON.stringify(state.savedConfigs),
    );
  }, [state.savedConfigs]);

  return (
    <CallerContext.Provider value={{ state, dispatch }}>
      {children}
    </CallerContext.Provider>
  );
}

export function useCallerStore() {
  const ctx = useContext(CallerContext);
  if (!ctx)
    throw new Error('useCallerStore must be used within CallerProvider');
  return ctx;
}

export type { CallerState, WorkingConfig };
